import { ArrayUtil, getThemeColor, getThemeForKey, HorizontalAlignment, Styles } from "@mcleod/core";
import { ClickEvent } from "../../events/ClickEvent";
import { ComponentStyles } from "../../base/ComponentStyles";
import { PanelProps } from "../panel/PanelProps";
import { Tab } from "./Tab";
import { TabSelectorPanel } from "./TabSelectorPanel";

const TabSelectorPanelUnderlinedStyles = Styles.make("tsp", {
    // Note that we are using !important to override the element's style values
    hoverHorizontal: {
        "&:hover": {
            backgroundColor: `${getThemeColor("primary.lightest")} !important`,
            // The '80' below is hex for 50%, results in the border bottom color having 50% opacity
            borderBottomColor: `${getThemeColor("primary")}80 !important`
        }
    },
    hoverVertical: {
        "&:hover": {
            backgroundColor: `${getThemeColor("primary.lightest")} !important`
        }
    }
});

export class TabSelectorPanelUnderlined extends TabSelectorPanel {
    private static readonly themeObject = getThemeForKey("tabset.headingUnderlined") ?? {};
    private static readonly themeObjectSelectedHorizontal =
        getThemeForKey("tabset.headingUnderlinedSelectedHorizontal") ?? {};
    private static readonly themeObjectUnselectedHorizontal =
        getThemeForKey("tabset.headingUnderlinedUnselectedHorizontal") ?? {};
        private static readonly themeObjectSelectedVertical =
        getThemeForKey("tabset.headingUnderlinedSelectedVertical") ?? {};
    private static readonly themeObjectUnselectedVertical =
        getThemeForKey("tabset.headingUnderlinedUnselectedVertical") ?? {};

    constructor(tab: Tab, props?: Partial<PanelProps>) {
        const isVerticalTabset = tab.isVertical();
        super(tab, {
            ...TabSelectorPanelUnderlined.themeObject,
            align: isVerticalTabset ? HorizontalAlignment.LEFT : HorizontalAlignment.CENTER,
            borderBottomWidth: isVerticalTabset ? 0 : 2,
            minHeight: isVerticalTabset ? 40 : null,
            paddingBottom: isVerticalTabset ? 8 : 4,
            paddingLeft: isVerticalTabset ? 16 : 18,
            paddingRight: isVerticalTabset ? 12 : 18,
            paddingTop: isVerticalTabset ? 8 : 4,
            rowBreak: isVerticalTabset,
            ...props
        });
        this.setClassIncluded(ComponentStyles.hoverShade);
        this.addClickListener((event: ClickEvent) => {
            this.tab.select(event);
            this.tab.selectInDesigner();
        });
        this.syncBorder();
    }

    override syncStyle() {
        super.syncStyle();
        this.applyVerticalAlignWorkaround();
        this.syncBorder();
    }

    /**
     * Apply styling directly to the panel's internal row to get around the shortcomings of how panels align elements
     * vertically.
     */
    private applyVerticalAlignWorkaround() {
        if (this.tab.isVertical()) {
            const panelRow: HTMLElement = ArrayUtil.getFirstElement(this.rows);
            if (panelRow != null) {
                panelRow.style.marginBottom = "auto";
                panelRow.style.marginTop = "auto";
            }
        }
    }

    protected override applyHoverClass() {
        const tabIsSelected = this.tab.selected;
        // Replace normal component hover class with class specific to underlined tabset selectors
        this.setClassIncluded(ComponentStyles.hoverShade, false);
        const cssClass = this.tab.isVertical() ?
            TabSelectorPanelUnderlinedStyles.hoverVertical :
            TabSelectorPanelUnderlinedStyles.hoverHorizontal;
        this.setClassIncluded(cssClass, !tabIsSelected);
    }

    syncBorder() {
        if (this.tab.isVertical()) {
            const shouldDisplayBorder = this.tab.displaysDividerBorder;
            this.setProps({
                borderBottomColor: shouldDisplayBorder ? "subtle.light" : null,
                borderBottomWidth: shouldDisplayBorder ? 1: 0
            });
        } else {
            const tabIsSelected = this.tab.selected;
            this.borderBottomColor = tabIsSelected ? "primary" : "subtle.light";
        }
    }

    protected override get selectedThemeObject(): any {
        return this.tab.isVertical() ?
            TabSelectorPanelUnderlined.themeObjectSelectedVertical :
            TabSelectorPanelUnderlined.themeObjectSelectedHorizontal;
    }

    protected override get unselectedThemeObject(): any {
        return this.tab.isVertical() ?
            TabSelectorPanelUnderlined.themeObjectUnselectedVertical :
            TabSelectorPanelUnderlined.themeObjectUnselectedHorizontal;
    }
}
